import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import winnerLogo from "@/assets/winnerLogo.svg";
import closeModal from "@/assets/closeModal.svg";
import winSound from "../../../assets/winning sound and cheering.mp3";
import winningGif from "../../../assets/merv_win.gif";
import arrow from "@/assets/arrow.png";

interface WinModalProps {
  open: boolean;
  onClose: () => void;
  countIndex: number;
  winnerPrize: any[];
  ticketNumber: number;
  hasLotteryWin: boolean;
  hasFreeTicketWin: boolean;
}

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(8px);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0.5rem;
`;

const ModalContent = styled.div`
  background: linear-gradient(145deg, rgba(5, 5, 24, 0.98), rgba(10, 10, 40, 0.98));
  border-radius: 20px;
  max-width: 600px;
  width: 90%;
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  padding: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 70%;
    gap: 0.75rem;
    padding: 1rem;
  }

  @media (max-width: 480px) {
    max-width: 80%;
    padding: 0.5rem;
    gap: 0.5rem;
    border-radius: 16px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  img {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    top: 0.5rem;
    right: 0.5rem;
    img {
      width: 14px;
      height: 14px;
    }
  }
`;

const WinnerLogo = styled.img`
  width: 100px;
  height: 100px;
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.25));
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
  }

  @media (max-width: 1024px) {
    width: 80px;
    height: 80px;
  }
`;

const WinModal: React.FC<WinModalProps> = ({
  open,
  onClose,
  countIndex,
  winnerPrize,
  ticketNumber,
  hasLotteryWin,
  hasFreeTicketWin
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fullModal = hasLotteryWin && hasFreeTicketWin;
  const lotteryTicketModal = hasLotteryWin && !hasFreeTicketWin;
  const freeTicketModal = !hasLotteryWin && hasFreeTicketWin;

  useEffect(() => {
    if (open) {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {
          console.log('Audio autoplay was prevented');
        });
      }
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [open]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      <audio ref={audioRef} src={winSound} />
      <Modal isOpen={open} onClick={handleOverlayClick}>
        <ModalContent>
          {/* <CloseButton onClick={onClose}>
            <img src={closeModal} alt="Close" />
          </CloseButton> */}

          {/* Left Section - Animation */}
          <div
            style={{
              height:
                window.innerWidth > 1200
                  ? '320px'
                  : window.innerWidth > 900
                    ? '240px'
                    : window.innerWidth > 768
                      ? '180px'
                      : window.innerWidth > 480
                        ? '140px'
                        : '110px',
              width:
                window.innerWidth > 1200
                  ? '320px'
                  : window.innerWidth > 900
                    ? '240px'
                    : window.innerWidth > 768
                      ? '180px'
                      : '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '16px',
              overflow: 'hidden',
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: '0 0 20px rgba(60, 55, 255, 0.25)'
            }}
          >
            <img
              src={winningGif}
              alt="Congratulations"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>

          {/* Right Section */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: window.innerWidth > 480 ? '1rem' : '0.5rem',
            padding: window.innerWidth > 480 ? '0.5rem' : '0.25rem'
          }}>
            <div style={{
              display: 'flex',
              gap: window.innerWidth > 480 ? '0.75rem' : '0.5rem',
              width: '100%',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {(fullModal || lotteryTicketModal) && (
                <div style={{
                  flex: '1',
                  minWidth: window.innerWidth > 768 ? '150px' : window.innerWidth > 480 ? '120px' : '100px',
                  background: 'linear-gradient(135deg, #4540FF, #3C37FF)',
                  borderRadius: window.innerWidth > 480 ? '12px' : '8px',
                  padding: window.innerWidth > 480 ? '1rem' : '0.5rem',
                  color: 'white',
                  boxShadow: '0 4px 15px rgba(60, 55, 255, 0.35)'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <h3 style={{
                      fontSize: window.innerWidth > 768 ? '20px' : window.innerWidth > 480 ? '18px' : '14px',
                      margin: '0 0 0.5rem 0',
                      fontWeight: 'bold'
                    }}>YOU WIN!</h3>
                    <div>
                      <p style={{
                        fontSize: window.innerWidth > 480 ? '11px' : '10px',
                        color: 'rgba(255, 255, 255, 0.8)',
                        margin: '0'
                      }}>Ticket no.</p>
                      <p style={{
                        fontSize: window.innerWidth > 768 ? '20px' : window.innerWidth > 480 ? '18px' : '14px',
                        margin: '0.25rem 0 0 0',
                        fontWeight: 'bold'
                      }}>{ticketNumber}</p>
                    </div>
                  </div>
                </div>
              )}
              {(fullModal || lotteryTicketModal) && (
                <div style={{
                  flex: '1',
                  minWidth: window.innerWidth > 768 ? '150px' : window.innerWidth > 480 ? '120px' : '100px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: window.innerWidth > 480 ? '12px' : '8px',
                  padding: window.innerWidth > 480 ? '1rem' : '0.5rem',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center'
                }}>
                  <h3 style={{
                    fontSize: window.innerWidth > 480 ? '16px' : '13px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    margin: '0 0 0.5rem 0'
                  }}>Winning Amount</h3>
                  <h2 style={{
                    fontSize: window.innerWidth > 768 ? '28px' : window.innerWidth > 480 ? '24px' : '18px',
                    margin: '0',
                    background: 'linear-gradient(135deg, #fff, #e0e0ff)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>$ {winnerPrize}</h2>
                </div>
              )}
            </div>
            {(fullModal || freeTicketModal) && (
              <div style={{
                fontSize: window.innerWidth > 768 ? '24px' : window.innerWidth > 480 ? '20px' : '16px',
                color: 'white',
                textAlign: 'center',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                <span style={{
                  fontSize: window.innerWidth > 768 ? '30px' : window.innerWidth > 480 ? '26px' : '20px',
                  display: 'block',
                  color: '#3C37FF',
                  marginBottom: '0.25rem'
                }}>{fullModal ? '+1' : '1'}</span>
                FREE TICKET
              </div>
            )}
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export default WinModal;